import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { BilledPrice, Me, Subscription, SubscriptionPlan, WebPrice } from '@cribbage/protocol';
import {
  type BillingInterval,
  type BillingStore,
  type PurchaseResult,
  billingStore,
} from '~/session/commerce.js';
import { useAuthStore } from '~/store/authStore.js';
import { entitlementsOf } from '~/store/membership.js';
import type { MenuTileSuit } from '~/ui/MenuTile.js';
import { useLatestRequest } from '~/ui/useLatestRequest.js';
import { accountPath, appPath, resumeState } from './accountPages.js';

/**
 * The Subscription group's pages, what they read off `/me`, and the plans.
 *
 * Drawn to the `lobby-subscription-settings`, `lobby-offer` and
 * `lobby-settings` sketches. The group page stands in the settings sidebar;
 * the others are solo pages with Back, as the Account group's are
 * (`SettingsScreen.tsx`). Every store action goes through
 * `session/commerce.ts`, and nothing here shows a plan as owned unless `/me`
 * says so.
 */

export const SUBSCRIPTION_PATH = '/settings/subscription';

/** Declared once: the type is read off the list, so the route guard cannot miss a page. */
const SUB_PAGES = ['offer', 'plans', 'subscribed', 'restore', 'change-plan'] as const;

export type SubscriptionSubPage = (typeof SUB_PAGES)[number];

export function isSubscriptionSubPage(page: string): page is SubscriptionSubPage {
  return (SUB_PAGES as readonly string[]).includes(page);
}

export function subscriptionPath(page: SubscriptionSubPage): string {
  return `${SUBSCRIPTION_PATH}/${page}`;
}

/**
 * What the upgrade pages carry from one to the next in router state.
 *
 * `backTo` is where the offer was opened from when that was outside Settings,
 * such as the out-of-credits sheet over a table. Back on the offer returns
 * there, and so does Done after a purchase. `restoreFrom` says which page's
 * Purchases band opened restore, so its Back lands there. `returnTo` is the
 * settings focus convention: the control the page was left from.
 *
 * `backFocus` is the `returnTo` the page outside handed the offer: the chip
 * that opened it, such as a picker's See the offer. It rides along with
 * `backTo`, and the way back out hands it back, so focus lands on that chip
 * rather than the heading (`docs/dev/subscriptions.md`, The client).
 */
export interface SubscriptionState {
  readonly backTo?: string | undefined;
  readonly backFocus?: string | undefined;
  readonly restoreFrom?: 'offer' | undefined;
  readonly returnTo?: string | undefined;
}

/** A focus key: a short plain word. Anything else in router state is dropped. */
function focusKey(value: unknown): string | undefined {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(value) ? value : undefined;
}

export function subscriptionState(state: unknown): SubscriptionState {
  const raw = (state ?? {}) as Record<string, unknown>;
  // Only a path inside the app: Back must never be sent off the site.
  const backTo = appPath(raw.backTo);
  const returnTo = focusKey(raw.returnTo);
  const ours = (Object.values(FOCUS) as string[]).includes(returnTo ?? '');
  return {
    backTo,
    // Arriving from outside, the caller's `returnTo` is the chip to come back
    // to. Once the pages carry it on, it travels as `backFocus`.
    backFocus:
      backTo === undefined ? undefined : (focusKey(raw.backFocus) ?? (ours ? undefined : returnTo)),
    restoreFrom: raw.restoreFrom === 'offer' ? 'offer' : undefined,
    returnTo,
  };
}

/** What the upgrade pages hand each other to keep the way back out. */
export function onward(state: SubscriptionState): Record<string, unknown> {
  return { backTo: state.backTo, backFocus: state.backFocus };
}

/**
 * The way back out of the upgrade cycle: the page outside that opened the
 * offer, with focus on its chip, or else Subscription.
 */
export function wayOut(state: SubscriptionState): {
  readonly to: string;
  readonly returnTo?: string | undefined;
} {
  return state.backTo === undefined
    ? { to: SUBSCRIPTION_PATH }
    : { to: state.backTo, returnTo: state.backFocus };
}

/**
 * A headline in the display face: the plan's name, Playing free, a restore
 * result, Subscribed's welcome. Display text is tracked tight (BRAND, Type).
 */
export const HEADLINE = 'font-display text-3xl leading-none font-extrabold tracking-tight';

/**
 * The offer, opened from outside Settings, such as the out-of-credits sheet
 * over a table. `backTo` is the address its Back and Done return to. Read `to`
 * and `state` for a `navigate`: a game surface cannot import these pages, so
 * the screen that owns it hands this down.
 */
export function offerFrom(backTo: string): {
  readonly to: string;
  readonly state: SubscriptionState;
} {
  return { to: subscriptionPath('offer'), state: { backTo } };
}

/**
 * A guest's Sign in, which comes back to `to` with `state` once the account is
 * made or signed in (`docs/ux/commerce/purchase.md`). Spread onto a link, or
 * read `to` and `state` for a `navigate`.
 */
export function signInFirst(
  to: string,
  state: Record<string, unknown> = {},
): {
  readonly to: string;
  readonly state: ReturnType<typeof resumeState>;
  readonly 'data-focus-key': string;
} {
  return {
    to: accountPath('sign-in'),
    state: resumeState(to, { ...state, returnTo: FOCUS.signIn }),
    'data-focus-key': FOCUS.signIn,
  };
}

/** The focus keys the pages' controls wear, so a way back can land on them. */
export const FOCUS = {
  offer: 'offer',
  restore: 'restore',
  manage: 'manage',
  subscribe: 'subscribe',
  signIn: 'sign-in',
} as const;

/* ------------------------------------------------------------------ plans -- */

/** The four plans in the order the pages draw them, each with its door's suit. */
export const PLANS: readonly { readonly id: SubscriptionPlan; readonly suit: MenuTileSuit }[] = [
  { id: 'surface', suit: 'spade' },
  { id: 'comms', suit: 'heart' },
  { id: 'improver', suit: 'diamond' },
  { id: 'bundle', suit: 'club' },
];

/**
 * The apps' prices, until StoreKit and Play Billing hand the pages the store's
 * own (`brain/decisions.md`, 2026-10-01 and 2026-10-08). The web never shows
 * these for a purchase: it shows `/me`'s `web_prices`, the catalog Checkout
 * charges, so an operator's new price reaches the page and Checkout together.
 */
const APP_CENTS: Readonly<Record<SubscriptionPlan, Readonly<Record<BillingInterval, number>>>> = {
  surface: { month: 99, year: 999 },
  comms: { month: 99, year: 999 },
  improver: { month: 199, year: 1999 },
  bundle: { month: 299, year: 2999 },
};

/**
 * A price a page shows. `id` is the web catalog's, sent back with the purchase
 * so the server refuses a price replaced since (`price_changed`). The apps'
 * prices have none.
 */
export interface ShownPrice {
  readonly cents: number;
  readonly id?: string | undefined;
}

/** The web store's prices on `/me`. A `/me` cached before they existed has none. */
export function webPricesOf(user: Me | null): readonly WebPrice[] {
  return (user?.web_prices as readonly WebPrice[] | undefined) ?? [];
}

/**
 * The price this device sells a plan at, or null when it does not sell it. On
 * the web that is a plan the catalog has off sale, or any plan on a `/me`
 * from before the web's prices were sent.
 */
export function priceOf(
  user: Me | null,
  store: BillingStore,
  plan: SubscriptionPlan,
  interval: BillingInterval,
): ShownPrice | null {
  if (store !== 'web') return { cents: APP_CENTS[plan][interval] };
  const price = webPricesOf(user).find(
    (entry) => entry.plan === plan && entry.interval === interval,
  );
  return price === undefined ? null : { cents: price.amount_cents, id: price.id };
}

/**
 * The cheapest plan this device sells at one interval: the offer's "from"
 * price. Null when it sells none, and the offer then names no price.
 */
export function cheapest(
  user: Me | null,
  store: BillingStore,
  interval: BillingInterval,
): number | null {
  const prices = PLANS.flatMap(({ id }) => priceOf(user, store, id, interval)?.cents ?? []);
  return prices.length === 0 ? null : Math.min(...prices);
}

/**
 * What a member pays each interval. On the web that is the price their
 * subscription bills, which they keep after the plan's price is replaced.
 * Without one (another store, or a `/me` from before prices) the apps' list
 * stands in.
 */
export function billedCents(subscription: BilledSubscriptionView): number {
  return subscription.price?.amount_cents ?? APP_CENTS[subscription.plan][intervalOf(subscription)];
}

const DOLLARS = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** A price as the store would write it: `$0.99`. */
export function formatPrice(cents: number): string {
  return DOLLARS.format(cents / 100);
}

/**
 * A price split for the display face: the dollars big, the cents a step
 * smaller. Read off the formatter's own parts, so the symbol and separator are
 * the formatter's rather than ours.
 */
export function priceParts(cents: number): { readonly whole: string; readonly fraction: string } {
  const parts = DOLLARS.formatToParts(cents / 100);
  const at = parts.findIndex(({ type }) => type === 'decimal');
  const join = (list: typeof parts): string => list.map(({ value }) => value).join('');
  return at < 0
    ? { whole: join(parts), fraction: '' }
    : { whole: join(parts.slice(0, at)), fraction: join(parts.slice(at)) };
}

/* ---------------------------------------------------------------- standing -- */

/**
 * The one change a member has booked for the end of the period. A `/me`
 * cached before the field existed has none, and missing means none.
 */
export type PendingChange = NonNullable<Subscription['pending_change']>;

/**
 * `subscription` as the screens read it: the protocol's fields, with the
 * interval, the price and the booked change optional. A `/me` cached before
 * them has nothing booked, its interval is read off the store (`intervalOf`),
 * and its price off the apps' list (`billedCents`).
 */
export type SubscriptionView = Omit<Subscription, 'interval' | 'pending_change' | 'price'> & {
  readonly interval?: BillingInterval | undefined;
  readonly price?: BilledPrice | null | undefined;
  readonly pending_change?: PendingChange | null | undefined;
};

/**
 * Where a player stands, which is what the Subscription page draws.
 *
 * - `guest`: no account, so no subscription (D29).
 * - `free`: an account that never subscribed.
 * - `renewing`: paid through a date and renewing past it.
 * - `ending`: paid through a date, with renewal off.
 * - `unpaid`: a renewal failed and the grace period holds the benefits.
 * - `complimentary`: Friends & Family, which an operator granted and no store
 *   bills, so nothing renews and nothing can be managed (Mitch, 2026-10-09).
 * - `ended`: it ran out.
 */
export type Standing =
  'guest' | 'free' | 'renewing' | 'ending' | 'unpaid' | 'complimentary' | 'ended';

/**
 * Read off `/me`. A cached `/me` from before subscriptions has no
 * `subscription` at all, and that reads as free (`docs/dev/subscriptions.md`).
 */
export function standingOf(user: Me | null): Standing {
  if (user === null || user.anonymous) return 'guest';
  const subscription = subscriptionOf(user);
  if (subscription === null) return 'free';
  if (subscription.status === 'expired') return 'ended';
  if (subscription.store === 'comp') return 'complimentary';
  if (subscription.status === 'grace') return 'unpaid';
  return subscription.renews ? 'renewing' : 'ending';
}

export function subscriptionOf(user: Me | null): SubscriptionView | null {
  return (user?.subscription as SubscriptionView | null | undefined) ?? null;
}

/** A member holds a plan right now: paid through, in grace, or granted. */
export function isMember(standing: Standing): boolean {
  return (
    standing === 'renewing' ||
    standing === 'ending' ||
    standing === 'unpaid' ||
    standing === 'complimentary'
  );
}

/**
 * A subscription a store bills. Every store row is one, and a comp is not. The server
 * pairs the comp store with the Friends & Family plan, so a billed row holds a sold plan.
 */
export type BilledSubscriptionView = SubscriptionView & {
  readonly store: Exclude<Subscription['store'], 'comp'>;
  readonly plan: SubscriptionPlan;
};

/** The subscription as a store bills it, or null for a comp, which nothing bills. */
export function billedOf(subscription: SubscriptionView): BilledSubscriptionView | null {
  return subscription.store === 'comp' ? null : (subscription as BilledSubscriptionView);
}

/**
 * The interval a subscription bills at. Until `/me` sends `interval`, it is
 * read off the store: the web (Stripe) sells yearly only, and the apps'
 * stores bill monthly (`brain/decisions.md` 2026-10-08).
 */
export function intervalOf(subscription: SubscriptionView): BillingInterval {
  return subscription.interval ?? (subscription.store === 'stripe' ? 'year' : 'month');
}

/** The store a purchase was made in, named the way the device stores are. */
export function storeOf(subscription: Pick<BilledSubscriptionView, 'store'>): BillingStore {
  return subscription.store === 'stripe' ? 'web' : subscription.store;
}

/* ----------------------------------------------------------------- dates -- */

/** A date from `/me`, written out long. Empty for a date the server did not send. */
export function longDate(iso: string | null | undefined, language: string): string {
  const date = new Date(iso ?? '');
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(language, { dateStyle: 'long' }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/* --------------------------------------------------------------- preview -- */

/** Where the restore page is: as opened, asking, or one of the five answers. */
export type RestoreStep =
  'ready' | 'checking' | 'found' | 'none' | 'pending' | 'failed' | 'unavailable';

/**
 * A page drawn in a state it would otherwise reach only by talking to a store.
 *
 * For the workshop, which stands each page in each state with no store behind
 * it, the way `ActionMenu`'s `defaultOpen` shows its menu without a press. The
 * app never provides one, so every field is absent there and each page starts
 * as it opens.
 */
export interface SubscriptionPreview {
  /** Which store the device sells through, in place of the platform's answer. */
  readonly store?: BillingStore;
  /** Restore drawn at this point, as if Restore had been pressed and answered. */
  readonly restore?: RestoreStep;
  /** The line a store action leaves, as if the seam had answered this. */
  readonly outcome?: PurchaseResult['status'];
  /** The Manage menu starts open. */
  readonly menuOpen?: boolean;
}

export const SubscriptionPreviewContext = createContext<SubscriptionPreview>({});

export function useSubscriptionPreview(): SubscriptionPreview {
  return useContext(SubscriptionPreviewContext);
}

/** The store this device sells through, or the workshop's stand-in for it. */
export function useBillingStore(): BillingStore {
  return useSubscriptionPreview().store ?? billingStore();
}

/* ------------------------------------------------------------ store calls -- */

/** What a store call that threw stands for. */
export const CALL_FAILED: PurchaseResult = { status: 'failed' };

/** The interval a device opens the plans on: yearly on the web, monthly in the apps. */
export function defaultInterval(store: BillingStore): BillingInterval {
  return store === 'web' ? 'year' : 'month';
}

/** A store answer the page states in a line, or nothing to say. */
export type Outcome = PurchaseResult['status'] | null;

export interface StoreCall {
  /** True while a call is out: the page says so and refuses a second press. */
  readonly busy: boolean;
  /**
   * The last answer the page states, as one line under its controls. It starts
   * as the workshop's preview, if any, and clears when a new call starts.
   */
  readonly outcome: Outcome;
  /** Set the line: an answer from `settle`, or null to clear it. */
  readonly show: (outcome: Outcome) => void;
  /**
   * Make one store call and settle its answer, once at a time.
   *
   * `fallback` stands in for an answer when the call throws, and is settled
   * in its place when `settle` itself throws. `settle` runs only while the
   * page still stands and the same player is signed in, and it gets that same
   * test back for after its own waits.
   */
  readonly run: <T>(
    call: () => Promise<T>,
    fallback: T,
    settle: (answer: T, ours: () => boolean) => Promise<void> | void,
  ) => void;
}

/**
 * One store call at a time, owned by the page that made it.
 *
 * A second press while one is out does nothing. The answer is dropped if the
 * page has gone, or if another player signed in while it was out: a purchase
 * must never be shown to somebody else (`docs/ux/commerce/purchase.md`).
 */
export function useStoreCall(): StoreCall {
  const request = useLatestRequest();
  const [busy, setBusy] = useState(false);
  const [outcome, show] = useState<Outcome>(useSubscriptionPreview().outcome ?? null);
  const flying = useRef(false);
  useEffect(() => request.retire, [request]);
  const run = useCallback<StoreCall['run']>(
    (call, fallback, settle) => {
      if (flying.current) return;
      flying.current = true;
      setBusy(true);
      show(null);
      const live = request.begin();
      const player = useAuthStore.getState().user?.id;
      const ours = (): boolean => live() && useAuthStore.getState().user?.id === player;
      void (async () => {
        let answer = fallback;
        try {
          answer = await call();
        } catch {
          // The seam threw rather than answering: that is its failure.
        }
        try {
          if (ours()) await settle(answer, ours);
        } catch {
          /* Settling threw, such as a `/me` re-read that failed. Nothing the
             store said can be believed now, so the page settles as if the call
             had failed: the fallback, which every page states truthfully. */
          try {
            if (ours()) await settle(fallback, ours);
          } catch {
            if (ours()) show('failed');
          }
        } finally {
          flying.current = false;
          if (live()) setBusy(false);
        }
      })();
    },
    [request],
  );
  return { busy, outcome, show, run };
}

/**
 * Read `/me` again after a store says yes, and answer whether it now shows a
 * member. The store's word is not enough: only `/me` says what is owned.
 */
export async function confirmedMember(): Promise<boolean> {
  await useAuthStore.getState().refresh();
  return isMember(standingOf(useAuthStore.getState().user));
}

/** Whether `/me` grants this player anything a plan brings. */
function grantsAnything(user: Me | null): boolean {
  return Object.values(entitlementsOf(user)).some(Boolean);
}

/**
 * How a page back from Checkout asks `/me` for the plan: quickly at first,
 * since the webhook usually lands within seconds, then slowly for a few
 * minutes, since Stripe retries a webhook that failed to deliver.
 */
export const CHECKOUT_POLLS = 6;
export const CHECKOUT_POLL_MS = 2000;
export const CHECKOUT_SLOW_POLLS = 20;
export const CHECKOUT_SLOW_POLL_MS = 15_000;

/**
 * Back from Stripe Checkout (`?checkout=done`, the server's
 * `ClientApp.subscription_url`). The webhook that writes the plan can land
 * after the player does, so the first `/me` may not show it yet. This asks
 * again, quickly and then slowly for about five minutes, and stops once `/me`
 * grants something, the page goes, or the polls run out. It reads the
 * entitlements, which the server works out from the paid-through date, not
 * the row's status, which a missed cancellation can leave `active`. True
 * while the page came back from Checkout and `/me` grants nothing yet, so the
 * page says the purchase is not confirmed rather than Playing free.
 */
export function useCheckoutReturn(): boolean {
  const { search } = useLocation();
  const back = new URLSearchParams(search).get('checkout') === 'done';
  const member = useAuthStore((state) => grantsAnything(state.user));
  const request = useLatestRequest();
  useEffect(() => {
    if (!back) return undefined;
    const live = request.begin();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const wait = (ms: number): Promise<void> =>
      new Promise((resolve) => {
        timer = setTimeout(resolve, ms);
      });
    void (async () => {
      for (let poll = 0; poll < CHECKOUT_POLLS + CHECKOUT_SLOW_POLLS; poll += 1) {
        await wait(poll < CHECKOUT_POLLS ? CHECKOUT_POLL_MS : CHECKOUT_SLOW_POLL_MS);
        if (!live()) return;
        await useAuthStore.getState().refresh();
        if (!live() || grantsAnything(useAuthStore.getState().user)) return;
      }
    })();
    return () => {
      request.retire();
      if (timer !== null) clearTimeout(timer);
    };
  }, [back, request]);
  return back && !member;
}
